import React, { useState, useEffect, useCallback } from "react";
import { useScrollLock } from "../hooks/useScrollLock";
import { useAuth } from "../context/AuthContext";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import {
  getReadings, getEmployees, updateReading, deleteReading,
  getManualMeters, createManualMeter, createManualMetersBatch,
  updateManualMeter, deleteManualMeter,
  getAdminMetersList,
} from "../services/api";
import { cloudinaryThumb, cloudinaryPreview } from "../services/cloudinary";
import DeleteConfirmModal from "../components/DeleteConfirmModal";
import * as XLSX from "xlsx";
import {
  Search, X, Plus, Edit2, Trash2,
  ChevronLeft, ChevronRight, Download, MapPin,
} from "lucide-react";

// ── Helpers ───────────────────────────────────────────────────────────────────
function fmt(d) {
  try { return format(new Date(d), "dd/MM/yyyy HH:mm", { locale: es }); }
  catch { return d; }
}

const PAGE_SIZE = 50;
const EMPTY_FORM = { meter_code: "", address: "", latitude: "", longitude: "", notes: "" };

// ══════════════════════════════════════════════════════════════════════════════
// Tab 1 — Lecturas
// ══════════════════════════════════════════════════════════════════════════════
function LecturasTab({ isAdminPlus }) {
  const [rows, setRows]         = useState([]);
  const [total, setTotal]       = useState(0);
  const [page, setPage]         = useState(1);
  const [loading, setLoading]   = useState(true);
  const [employees, setEmployees] = useState([]);
  const [lightbox, setLightbox] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [filters, setFilters]   = useState({ userId: "", from: "", to: "", meterCode: "" });
  const [limit, setLimit]       = useState(50);
  const totalPages = Math.max(1, Math.ceil(total / limit));

  useEffect(() => { getEmployees().then(setEmployees).catch(() => {}); }, []);

  const load = useCallback((p = 1) => {
    setLoading(true);
    getReadings({ ...filters, page: p, limit })
      .then((d) => { setRows(d.data); setTotal(d.pagination?.total || 0); setPage(p); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [filters, limit]);

  useEffect(() => { load(1); }, [load]);

  function applyFilters(e) { e.preventDefault(); load(1); }
  function clearFilters() { setFilters({ userId: "", from: "", to: "", meterCode: "" }); }
  const hasFilters = Object.values(filters).some(Boolean);

  function exportExcel() {
    const rows2 = rows.map((r) => ({
      Fecha: fmt(r.created_at),
      Auditor: r.user_name || "",
      "Código medidor": r.meter_code || "",
      Valor: r.reading_value || "",
      Latitud: r.latitude || "",
      Longitud: r.longitude || "",
      "Foto URL": r.image_url || "",
    }));
    const ws = XLSX.utils.json_to_sheet(rows2);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Lecturas");
    XLSX.writeFile(wb, `lecturas_${format(new Date(), "yyyyMMdd")}.xlsx`);
  }

  return (
    <>
      <DeleteConfirmModal
        visible={!!deleteTarget}
        message={`¿Eliminar esta lectura?`}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={async () => {
          await deleteReading(deleteTarget.id);
          setRows((p) => p.filter((r) => r.id !== deleteTarget.id));
          setTotal((t) => t - 1);
          setDeleteTarget(null);
        }}
      />

      {/* Barra superior */}
      <div style={s.topBar}>
        <form style={s.filterBar} onSubmit={applyFilters}>
          <select style={s.select} value={filters.userId} onChange={(e) => setFilters((f) => ({ ...f, userId: e.target.value }))}>
            <option value="">Todos los auditores</option>
            {employees.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
          </select>
          <input style={s.input} type="date" value={filters.from} onChange={(e) => setFilters((f) => ({ ...f, from: e.target.value }))} />
          <input style={s.input} type="date" value={filters.to}   onChange={(e) => setFilters((f) => ({ ...f, to:   e.target.value }))} />
          <div style={s.searchWrap}>
            <Search size={14} style={{ color: "var(--text-2)", flexShrink: 0 }} />
            <input style={s.searchInput} placeholder="Código medidor..." value={filters.meterCode}
              onChange={(e) => setFilters((f) => ({ ...f, meterCode: e.target.value }))} />
            {filters.meterCode && <button style={s.iconBtn} type="button" onClick={() => setFilters((f) => ({ ...f, meterCode: "" }))}><X size={13} /></button>}
          </div>
          <button style={s.btnPrimary} type="submit">Buscar</button>
          {hasFilters && <button style={s.btnGhost} type="button" onClick={clearFilters}><X size={14} /></button>}
        </form>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={s.totalBadge}>{total.toLocaleString("es-AR")}</span>
          <span style={s.totalLabel}>lecturas</span>
          {isAdminPlus && (
            <button style={s.btnExport} onClick={exportExcel}>
              <Download size={13} style={{ marginRight: 6 }} />Exportar
            </button>
          )}
        </div>
      </div>

      {/* Tabla */}
      <div style={s.card}>
        {loading ? <div style={s.empty}>Cargando...</div> : rows.length === 0 ? (
          <div style={s.empty}>Sin resultados</div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={s.table}>
              <thead>
                <tr>
                  {["Foto", "Fecha", "Auditor", "Código medidor", "Valor", "Ubicación", ...(isAdminPlus ? [""] : [])].map((h) => (
                    <th key={h} style={s.th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} style={s.tr}>
                    <td style={s.td}>
                      {r.image_url ? (
                        <img src={cloudinaryThumb(r.image_url, 48, 48)} alt="" style={s.thumb}
                          loading="lazy" onClick={() => setLightbox(cloudinaryPreview(r.image_url))} />
                      ) : <div style={s.noPhoto}>—</div>}
                    </td>
                    <td style={s.td}><span style={s.dateText}>{fmt(r.created_at)}</span></td>
                    <td style={s.td}><span style={s.nameText}>{r.user_name || "—"}</span></td>
                    <td style={s.td}><span style={s.codeText}>{r.meter_code || "—"}</span></td>
                    <td style={s.td}><span style={r.reading_value ? s.valueText : s.dimText}>{r.reading_value || "—"}</span></td>
                    <td style={s.td}>
                      {r.latitude && r.longitude ? (
                        <a href={`https://www.google.com/maps?q=${r.latitude},${r.longitude}`}
                          target="_blank" rel="noreferrer" style={s.mapLink}>
                          <MapPin size={14} style={{ marginRight: 4 }} />Ver
                        </a>
                      ) : <span style={s.dimText}>—</span>}
                    </td>
                    {isAdminPlus && (
                      <td style={s.td}>
                        <button style={s.btnDelete} onClick={() => setDeleteTarget(r)} title="Eliminar">
                          <Trash2 size={13} />
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Paginación */}
      {totalPages > 1 && (
        <div style={s.pagination}>
          <button style={s.pageBtn} disabled={page <= 1} onClick={() => load(page - 1)}><ChevronLeft size={16} /></button>
          <span style={s.pageInfo}>Página {page} de {totalPages}</span>
          <button style={s.pageBtn} disabled={page >= totalPages} onClick={() => load(page + 1)}><ChevronRight size={16} /></button>
        </div>
      )}

      {lightbox && (
        <div style={s.lightboxOverlay} onClick={() => setLightbox(null)}>
          <button style={s.lightboxClose} onClick={() => setLightbox(null)}><X size={20} color="#fff" /></button>
          <img src={lightbox} alt="" style={s.lightboxImg} onClick={(e) => e.stopPropagation()} />
        </div>
      )}
    </>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// Tab 2 — Medidores manuales (formulario inline + tabla)
// ══════════════════════════════════════════════════════════════════════════════
function ManualesTab({ isAdminPlus }) {
  const [rows, setRows]         = useState([]);
  const [total, setTotal]       = useState(0);
  const [page, setPage]         = useState(1);
  const [search, setSearch]     = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  // null = crear nuevo, id = editar ese
  const [editingId, setEditingId] = useState(null);
  const [form, setForm]         = useState(EMPTY_FORM);
  const [formError, setFormError] = useState("");
  const [saving, setSaving]     = useState(false);
  const [creationType, setCreationType] = useState("individual");
  const [barrioForm, setBarrioForm] = useState({ neighborhoodName: "", latitude: "", longitude: "", meterCodesList: "" });
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const load = useCallback(async (p = 1) => {
    setLoading(true); setError("");
    try {
      const res = await getManualMeters({ search: search || undefined, page: p, limit: PAGE_SIZE });
      setRows(res.data); setTotal(res.total); setPage(p);
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  }, [search]);

  useEffect(() => { load(1); }, [load]);

  function applySearch() { setSearch(searchInput.trim()); }
  function clearSearch() { setSearchInput(""); setSearch(""); }

  function parseMeterEntries(text) {
    return text.split(/\n/).map((line) => {
      const [rawCode, rawLote = ""] = line.split(",");
      return { meter_code: rawCode.trim(), lote: rawLote.trim() };
    }).filter((e) => e.meter_code);
  }

  function startEdit(row) {
    setEditingId(row.id);
    setForm({ meter_code: row.meter_code || "", address: row.address || "", latitude: row.latitude ?? "", longitude: row.longitude ?? "", notes: row.notes || "" });
    setFormError(""); setCreationType("individual");
  }
  function cancelEdit() { setEditingId(null); setForm(EMPTY_FORM); setFormError(""); setCreationType("individual"); setBarrioForm({ neighborhoodName: "", latitude: "", longitude: "", meterCodesList: "" }); }

  async function handleSave() {
    if (editingId === null && creationType === "barrio") {
      if (!barrioForm.neighborhoodName.trim()) { setFormError("El nombre del barrio es requerido."); return; }
      const entries = parseMeterEntries(barrioForm.meterCodesList);
      if (entries.length === 0) { setFormError("Ingresá al menos un medidor."); return; }
      setSaving(true); setFormError("");
      try {
        const res = await createManualMetersBatch({ entries, address: barrioForm.neighborhoodName.trim(), latitude: barrioForm.latitude !== "" ? barrioForm.latitude : null, longitude: barrioForm.longitude !== "" ? barrioForm.longitude : null });
        cancelEdit(); load(1);
        if (res.routeCreated) {
          setSuccessMsg(`✓ ${res.created} medidores creados. Ruta "${barrioForm.neighborhoodName.trim()}" generada en Rutas.`);
          setTimeout(() => setSuccessMsg(""), 6000);
        }
      } catch (err) { setFormError(err.message); }
      finally { setSaving(false); }
      return;
    }
    if (!form.meter_code.trim()) { setFormError("El código del medidor es requerido."); return; }
    setSaving(true); setFormError("");
    try {
      const payload = { meter_code: form.meter_code.trim(), address: form.address.trim() || null, latitude: form.latitude !== "" ? form.latitude : null, longitude: form.longitude !== "" ? form.longitude : null, notes: form.notes.trim() || null };
      if (editingId === null) await createManualMeter(payload);
      else { await updateManualMeter(editingId, payload); setEditingId(null); }
      setForm(EMPTY_FORM); setFormError(""); load(editingId === null ? 1 : page);
    } catch (err) { setFormError(err.message); }
    finally { setSaving(false); }
  }

  async function handleDelete() {
    setDeleting(true);
    try { await deleteManualMeter(deleteTarget.id); setDeleteTarget(null); load(page); }
    catch (err) { setError(err.message); }
    finally { setDeleting(false); }
  }

  const formTitle = editingId !== null ? "Editar medidor" : "Nuevo medidor";

  return (
    <>
      <DeleteConfirmModal visible={!!deleteTarget} message={`¿Eliminar el medidor "${deleteTarget?.meter_code}"?`}
        onCancel={() => setDeleteTarget(null)} onConfirm={handleDelete} />

      {error      && <div style={s.errorBox}>{error}</div>}
      {successMsg && <div style={s.successBox}>{successMsg}</div>}

      <div style={s.splitLayout}>
        {/* ── Formulario siempre visible ── */}
        {isAdminPlus && (
          <div style={s.formPanel}>
            <div style={s.formPanelHeader}>
              <span style={s.formPanelTitle}>{formTitle}</span>
              {editingId !== null && (
                <button style={s.btnGhost} onClick={cancelEdit}><X size={14} /></button>
              )}
            </div>

            {/* Selector tipo (solo al crear) */}
            {editingId === null && (
              <div style={{ display: "flex", gap: 6, marginBottom: 16 }}>
                {["individual", "barrio"].map((t) => (
                  <button key={t} style={{ ...s.typeBtn, flex: 1, ...(creationType === t ? s.typeBtnActive : {}) }} onClick={() => setCreationType(t)}>
                    {t === "individual" ? "Individual" : "Barrio / Lote"}
                  </button>
                ))}
              </div>
            )}

            {editingId === null && creationType === "barrio" ? (
              <>
                <label style={s.label}>Nombre del barrio *</label>
                <input style={s.inputField} value={barrioForm.neighborhoodName} onChange={(e) => setBarrioForm((f) => ({ ...f, neighborhoodName: e.target.value }))} placeholder="Ej: Barrio Norte" />
                <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                  <div style={{ flex: 1 }}>
                    <label style={s.label}>Latitud</label>
                    <input style={s.inputField} value={barrioForm.latitude} onChange={(e) => setBarrioForm((f) => ({ ...f, latitude: e.target.value }))} placeholder="-34.6037" />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={s.label}>Longitud</label>
                    <input style={s.inputField} value={barrioForm.longitude} onChange={(e) => setBarrioForm((f) => ({ ...f, longitude: e.target.value }))} placeholder="-58.3816" />
                  </div>
                </div>
                <label style={{ ...s.label, marginTop: 10 }}>Medidores (uno por línea: número, lote) *</label>
                <textarea style={{ ...s.inputField, minHeight: 140, resize: "vertical", fontFamily: "monospace", fontSize: 12 }}
                  value={barrioForm.meterCodesList} onChange={(e) => setBarrioForm((f) => ({ ...f, meterCodesList: e.target.value }))}
                  placeholder={"12345678, 1A\n87654321, 2B"} />
              </>
            ) : (
              <>
                <label style={s.label}>Código de medidor *</label>
                <input style={s.inputField} value={form.meter_code} onChange={(e) => setForm((f) => ({ ...f, meter_code: e.target.value }))} placeholder="Ej: 000000001" />
                <label style={{ ...s.label, marginTop: 10 }}>Dirección / Barrio</label>
                <input style={s.inputField} value={form.address} onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))} placeholder="Ej: Barrio Norte" />
                <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                  <div style={{ flex: 1 }}>
                    <label style={s.label}>Latitud</label>
                    <input style={s.inputField} value={form.latitude} onChange={(e) => setForm((f) => ({ ...f, latitude: e.target.value }))} placeholder="-34.6037" />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={s.label}>Longitud</label>
                    <input style={s.inputField} value={form.longitude} onChange={(e) => setForm((f) => ({ ...f, longitude: e.target.value }))} placeholder="-58.3816" />
                  </div>
                </div>
                <label style={{ ...s.label, marginTop: 10 }}>Notas</label>
                <input style={s.inputField} value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} placeholder="Opcional" />
              </>
            )}

            {formError && <div style={{ ...s.errorBox, marginTop: 10 }}>{formError}</div>}
            <button style={{ ...s.btnPrimary, width: "100%", marginTop: 14, justifyContent: "center" }} onClick={handleSave} disabled={saving}>
              {saving ? "Guardando..." : editingId !== null ? "Guardar cambios" : "Crear medidor"}
            </button>
          </div>
        )}

        {/* ── Lista ── */}
        <div style={{ flex: 1, minWidth: 0 }}>
          {/* Barra de búsqueda + conteo */}
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
            <div style={{ ...s.searchWrap, flex: 1 }}>
              <Search size={14} style={{ color: "var(--text-2)", flexShrink: 0 }} />
              <input style={s.searchInput} placeholder="Buscar por código o dirección..." value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && applySearch()} />
              {search ? <button style={s.iconBtn} onClick={clearSearch}><X size={13} /></button>
                : <button style={s.iconBtn} onClick={applySearch}><Search size={13} /></button>}
            </div>
            <span style={s.totalBadge}>{total.toLocaleString("es-AR")}</span>
            <span style={s.totalLabel}>medidores</span>
          </div>

          <div style={s.card}>
            {loading ? <div style={s.empty}>Cargando...</div> : rows.length === 0 ? (
              <div style={s.empty}>{search ? "Sin resultados." : "No hay medidores aún."}</div>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table style={s.table}>
                  <thead>
                    <tr>
                      {["Código", "Dirección / Barrio", "Ubicación", "Notas", ...(isAdminPlus ? [""] : [])].map((h) => (
                        <th key={h} style={s.th}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => (
                      <tr key={row.id} style={{ ...s.tr, ...(editingId === row.id ? { background: "var(--primary-bg)" } : {}) }}>
                        <td style={s.td}><span style={s.codeText}>{row.meter_code || "—"}</span></td>
                        <td style={s.td}><span style={s.nameText}>{row.address || <span style={s.dimText}>—</span>}</span></td>
                        <td style={s.td}>
                          {row.latitude && row.longitude ? (
                            <a href={`https://www.google.com/maps?q=${row.latitude},${row.longitude}`} target="_blank" rel="noreferrer" style={s.mapLink}>
                              <MapPin size={13} style={{ marginRight: 3 }} />Ver
                            </a>
                          ) : <span style={s.dimText}>—</span>}
                        </td>
                        <td style={s.td}><span style={s.dimText}>{row.notes || "—"}</span></td>
                        {isAdminPlus && (
                          <td style={s.td}>
                            <div style={{ display: "flex", gap: 5 }}>
                              <button style={s.btnEdit}   onClick={() => startEdit(row)}          title="Editar"><Edit2  size={13} /></button>
                              <button style={s.btnDelete} onClick={() => setDeleteTarget(row)}     title="Eliminar"><Trash2 size={13} /></button>
                            </div>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {totalPages > 1 && (
            <div style={s.pagination}>
              <button style={s.pageBtn} disabled={page <= 1} onClick={() => load(page - 1)}><ChevronLeft size={16} /></button>
              <span style={s.pageInfo}>Página {page} de {totalPages}</span>
              <button style={s.pageBtn} disabled={page >= totalPages} onClick={() => load(page + 1)}><ChevronRight size={16} /></button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// Página principal
// ══════════════════════════════════════════════════════════════════════════════
export default function MedidoresPage() {
  const { user } = useAuth();
  const isAdminPlus = user?.role === "admin-plus";
  const [tab, setTab] = useState("lecturas");

  return (
    <div style={{ width: "100%" }}>
      <div style={{ marginBottom: 4 }}>
        <h1 style={{ fontSize: 22, fontWeight: 800, color: "var(--text)", margin: 0, marginBottom: 16 }}>Medidores</h1>
      </div>

      <div style={s.tabBar}>
        <button style={{ ...s.tab, ...(tab === "lecturas" ? s.tabActive : {}) }} onClick={() => setTab("lecturas")}>
          Lecturas
        </button>
        <button style={{ ...s.tab, ...(tab === "manuales" ? s.tabActive : {}) }} onClick={() => setTab("manuales")}>
          Manuales
        </button>
      </div>

      <div>
        {tab === "lecturas" && <LecturasTab isAdminPlus={isAdminPlus} />}
        {tab === "manuales" && <ManualesTab isAdminPlus={isAdminPlus} />}
      </div>
    </div>
  );
}

// ── Estilos compartidos ───────────────────────────────────────────────────────
const s = {
  tabBar: { display: "flex", gap: 4, borderBottom: "1px solid var(--border)", marginBottom: 24 },
  tab: { display: "flex", alignItems: "center", gap: 7, padding: "10px 20px", border: "none", background: "none", color: "var(--text-2)", fontWeight: 700, fontSize: 13, cursor: "pointer", fontFamily: "Montserrat, sans-serif", borderBottom: "2px solid transparent", marginBottom: -1, transition: "all .15s" },
  tabActive: { color: "#D4620D", borderBottom: "2px solid #D4620D" },

  topBar: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, marginBottom: 16, flexWrap: "wrap" },
  filterBar: { display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", flex: 1 },
  searchWrap: { display: "flex", alignItems: "center", gap: 6, background: "var(--bg-subtle)", borderRadius: 10, padding: "7px 10px", flex: 1, minWidth: 200 },
  searchInput: { flex: 1, border: "none", outline: "none", fontSize: 13, color: "var(--text)", background: "transparent", fontFamily: "Montserrat, sans-serif" },
  select: { padding: "8px 12px", borderRadius: 10, fontSize: 13, background: "var(--bg-subtle)", color: "var(--text)", fontFamily: "Montserrat, sans-serif", minWidth: 180 },
  input:  { padding: "8px 12px", borderRadius: 10, fontSize: 13, background: "var(--bg-subtle)", color: "var(--text)", fontFamily: "Montserrat, sans-serif" },
  iconBtn: { background: "none", border: "none", cursor: "pointer", color: "var(--text-2)", display: "flex", alignItems: "center", padding: 0 },
  btnPrimary: { display: "inline-flex", alignItems: "center", padding: "8px 16px", borderRadius: 10, border: "none", background: "#D4620D", color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "Montserrat, sans-serif" },
  btnGhost:   { padding: "8px 12px", borderRadius: 10, border: "none", background: "var(--bg-subtle)", color: "var(--text-2)", fontSize: 13, cursor: "pointer", display: "flex", alignItems: "center", fontFamily: "Montserrat, sans-serif" },
  btnExport:  { display: "inline-flex", alignItems: "center", padding: "8px 14px", borderRadius: 10, border: "none", background: "#16a34a", color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "Montserrat, sans-serif" },
  totalBadge: { background: "var(--primary-bg)", color: "#D4620D", borderRadius: 6, padding: "2px 10px", fontSize: 14, fontWeight: 700 },
  totalLabel: { fontSize: 13, color: "var(--text-2)", fontWeight: 600 },

  card: { borderRadius: 14, boxShadow: "0 1px 4px rgba(0,0,0,0.07)", overflow: "auto", marginBottom: 16 },
  empty: { padding: 48, textAlign: "center", color: "var(--text-2)", fontSize: 14 },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 13 },
  th: { padding: "12px 16px", textAlign: "left", fontWeight: 700, color: "var(--text-2)", fontSize: 12, textTransform: "uppercase", letterSpacing: "0.05em", borderBottom: "1px solid var(--border)", whiteSpace: "nowrap" },
  tr: { borderBottom: "1px solid var(--border)", transition: "background 0.1s" },
  td: { padding: "11px 16px", verticalAlign: "middle" },

  thumb:    { width: 44, height: 44, objectFit: "cover", borderRadius: 8, cursor: "zoom-in", display: "block" },
  noPhoto:  { color: "var(--text-3)", fontSize: 18, paddingLeft: 8 },
  dateText: { fontSize: 13, color: "var(--text-2)", whiteSpace: "nowrap" },
  nameText: { fontSize: 13, fontWeight: 700, color: "var(--text)" },
  codeText: { fontSize: 13, fontWeight: 700, color: "var(--text)" },
  valueText:{ fontSize: 13, fontWeight: 700, color: "#D4620D" },
  dimText:  { fontSize: 13, color: "var(--text-3)" },
  mapLink:  { display: "inline-flex", alignItems: "center", color: "#D4620D", textDecoration: "none", fontSize: 13, fontWeight: 700 },
  btnEdit:  { display: "inline-flex", alignItems: "center", justifyContent: "center", width: 30, height: 30, borderRadius: 8, border: "none", background: "var(--primary-bg)", color: "#D4620D", cursor: "pointer" },
  btnDelete:{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 30, height: 30, borderRadius: 8, border: "none", background: "var(--error-bg)", color: "var(--error-text)", cursor: "pointer" },

  pagination: { display: "flex", justifyContent: "center", alignItems: "center", gap: 12, marginTop: 8 },
  pageBtn:    { width: 36, height: 36, borderRadius: 10, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--text)", border: "1px solid var(--border)", background: "var(--bg-card)" },
  pageInfo:   { fontSize: 13, color: "var(--text-2)", fontWeight: 700 },

  errorBox:   { background: "var(--error-bg)", color: "var(--error-text)", borderRadius: 9, padding: "10px 14px", fontSize: 13, fontWeight: 600, marginBottom: 14 },
  successBox: { background: "var(--success-bg)", color: "var(--success-text)", borderRadius: 9, padding: "10px 14px", fontSize: 13, fontWeight: 600, marginBottom: 14 },

  // Modal
  modalOverlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 20 },
  modal:        { background: "var(--bg-card)", borderRadius: 14, padding: 28, width: "100%", maxWidth: 480, maxHeight: "88vh", overflowY: "auto" },
  modalHeader:  { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 },
  modalTitle:   { fontSize: 18, fontWeight: 700, color: "var(--text)", margin: 0 },
  modalClose:   { background: "none", border: "none", cursor: "pointer", color: "var(--text-2)", display: "flex", alignItems: "center" },
  label:        { display: "block", fontSize: 12, fontWeight: 700, color: "var(--text-2)", marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.05em" },
  inputField:   { width: "100%", padding: "10px 12px", borderRadius: 10, border: "1px solid var(--border)", fontSize: 13, fontFamily: "Montserrat, sans-serif", color: "var(--text)", background: "var(--bg)", outline: "none", boxSizing: "border-box" },
  typeBtn:      { flex: 1, padding: "8px 14px", borderRadius: 10, border: "1px solid var(--border)", background: "var(--bg-subtle)", color: "var(--text-2)", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "Montserrat, sans-serif" },
  typeBtnActive:{ background: "var(--primary-bg)", color: "#D4620D", borderColor: "#D4620D" },

  // Split layout manuales
  splitLayout:    { display: "flex", gap: 20, alignItems: "flex-start" },
  formPanel:      { width: 300, flexShrink: 0, background: "var(--bg-card)", borderRadius: 14, padding: 20, boxShadow: "0 1px 4px rgba(0,0,0,0.07)", position: "sticky", top: 0 },
  formPanelHeader:{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 },
  formPanelTitle: { fontSize: 14, fontWeight: 700, color: "var(--text)" },

  lightboxOverlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.92)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center" },
  lightboxClose:   { position: "absolute", top: 20, right: 20, background: "rgba(255,255,255,0.15)", border: "none", borderRadius: "50%", width: 40, height: 40, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" },
  lightboxImg:     { maxWidth: "90vw", maxHeight: "88vh", borderRadius: 10 },
};
